import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService, DOCUMENT_TYPES } from "@/lib/api";
import type { DocumentType } from "@/lib/api";

export interface PatientDocument {
  id: string;
  document_type: DocumentType;
  source: string;
  file: string;
  description: string;
  document_date: string | null;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface DocumentFilters {
  types?: DocumentType[];
  startTime?: string;
  endTime?: string;
  consolidated?: boolean;
  includeUncategorized?: boolean;
}

export const usePatientDocuments = (
  patientId: string | undefined,
  filters: DocumentFilters = {}
) => {
  const {
    types = [],
    startTime,
    endTime,
    consolidated = false,
    includeUncategorized,
  } = filters;

  return useQuery({
    queryKey: [
      "patient-documents",
      patientId,
      types,
      startTime,
      endTime,
      consolidated,
      includeUncategorized,
    ],
    queryFn: async () => {
      if (!patientId) return [];

      try {
        // Build API parameters
        const params: any = {
          consolidated,
        };

        if (startTime) params.start_time = startTime;
        if (endTime) params.end_time = endTime;

        // When "All" is selected (no specific type), include uncategorized
        if (types.length === 0) {
          params.include_uncategorized = includeUncategorized ?? true;
        } else {
          // Use the first (and only) type for API call
          params.type = types[0];
        }

        const response = await apiService.getPatientDocuments(
          patientId,
          params
        );
        // The API returns the array directly, not wrapped in a data property
        const documents = response.data || [];

        return documents;
      } catch (error) {
        console.error("Error fetching patient documents:", error);
        throw error;
      }
    },
    enabled: !!patientId,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
    retry: 1, // Only retry once on failure
  });
};

export const useUploadPatientDocument = (
  uploadId: string | undefined,
  patientId?: string
) => {
  const queryClient = useQueryClient();
  const cacheId = patientId || uploadId;

  return useMutation({
    mutationFn: async ({
      file,
      documentType,
      description,
    }: {
      file: File;
      documentType?: string;
      description?: string;
    }) => {
      if (!uploadId) throw new Error("Upload ID is required");
      return apiService.uploadPatientDocument(
        uploadId,
        file,
        documentType,
        description
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patient-documents", cacheId],
      });
    },
  });
};

// Helper function to get document type display name
export const getDocumentTypeName = (type: DocumentType): string => {
  return DOCUMENT_TYPES[type] || type;
};

// Helper function to get file extension from URL
export const getFileExtensionFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const extension = pathname.split(".").pop()?.toLowerCase() || "";
    return extension;
  } catch {
    return "";
  }
};

// Helper function to check if file is an image
export const isImageDocument = (url: string): boolean => {
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
  const extension = getFileExtensionFromUrl(url);
  return imageExtensions.includes(extension);
};
